import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { useAuth } from './auth/AuthProvider'

const TriggerPill = styled.button`
  height: 40px;
  padding: 0 1.25rem;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  z-index: ${theme.zIndex.navigation};
  box-shadow: 0 2px 8px ${theme.color.shadow.dark};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #b8002b;
    transform: translateY(-1px);
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 92%;
  max-width: 960px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #eee;
`

const Title = styled.h2`
  font-size: 1.7rem;
  margin: 0;
  color: ${theme.color.text.base};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.9rem;
  cursor: pointer;
  color: ${theme.color.text.grey};
`

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
  padding: 0 1.5rem;
`

const Tab = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${(p) => (p.active ? theme.color.accent.primary : theme.color.text.grey)};
  border-bottom: 3px solid ${(p) => (p.active ? theme.color.accent.primary : 'transparent')};
  margin-bottom: -1px;

  &:hover {
    color: ${theme.color.accent.primary};
  }
`

const Body = styled.div`
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  flex: 1;
`

const Row = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 0.75rem;
`

const Label = styled.label`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  display: block;
  margin-bottom: 0.25rem;
`

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1.1rem;
  min-width: 220px;
`

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1.1rem;
`

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1.1rem;
  width: 100%;
  min-height: 70px;
  resize: vertical;
  box-sizing: border-box;
`

const PrimaryButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 0.55rem 1.25rem;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    background: #aaa;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #b8002b;
  }
`

const SecondaryButton = styled.button`
  background: white;
  color: ${theme.color.text.base};
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1.05rem;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`

const ChallengeCard = styled.div`
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 0.75rem;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 1.2rem;
`

const CardMeta = styled.div`
  font-size: 0.95rem;
  color: ${theme.color.text.grey};
`

const ClassList = styled.ul`
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
  font-size: 1.05rem;
  color: ${theme.color.text.base};
`

const ClassRow = styled.li<{ checked?: boolean }>`
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid #f0f0f0;
  background: ${(p) => (p.checked ? '#fff8f8' : 'transparent')};
`

const ScheduleList = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  border: 1px solid #eee;
  border-radius: 4px;
  max-height: 280px;
  overflow-y: auto;
`

const RunningListBox = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border: 1px dashed #aaa;
  border-radius: 4px;
  background: #fafafa;
`

const ErrorBanner = styled.div`
  background: #ffe6e6;
  color: #b00020;
  padding: 0.6rem;
  border-radius: 4px;
  font-size: 1.05rem;
  margin-bottom: 0.75rem;
`

const SuccessBanner = styled.div`
  background: #e6ffea;
  color: #086e2a;
  padding: 0.6rem;
  border-radius: 4px;
  font-size: 1.05rem;
  margin-bottom: 0.75rem;
`

const HeldBanner = styled.div`
  background: #fff3cd;
  color: #684d03;
  border: 1px solid #ffe69c;
  padding: 0.6rem;
  border-radius: 4px;
  font-size: 1rem;
  margin: 0.5rem 0 0.75rem;
`

const Empty = styled.div`
  text-align: center;
  color: ${theme.color.text.grey};
  padding: 2rem;
  font-style: italic;
`

const StatusPill = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.status === 'active' ? '#fff3cd' :
    p.status === 'in_progress' ? '#d1ecf1' :
    p.status === 'held' ? '#fff3cd' :
    p.status === 'completed' ? '#d4edda' : '#eee'};
  color: ${(p) =>
    p.status === 'active' ? '#856404' :
    p.status === 'in_progress' ? '#0c5460' :
    p.status === 'held' ? '#684d03' :
    p.status === 'completed' ? '#155724' : '#333'};
`

// Studios in scope for Studio Challenges. US studios first (excluding
// Miami Beach 9616 and Midtown Miami 9615), then Canada / UK / Dublin.
const ALL_STUDIOS = [
  { name: 'Arlington, VA', id: 9657 },
  { name: 'Austin, TX', id: 9733 },
  { name: 'Aventura, FL', id: 9631 },
  { name: 'Back Bay, MA', id: 9632 },
  { name: 'Boca Raton, FL', id: 10231 },
  { name: 'Brentwood, CA', id: 9654 },
  { name: 'Brooklyn Heights, NY', id: 10030 },
  { name: 'Buckhead, GA', id: 9627 },
  { name: 'Burlingame, CA', id: 9641 },
  { name: 'Castro, CA', id: 9640 },
  { name: 'Charleston, SC', id: 10099 },
  { name: 'Charlotte, NC', id: 9651 },
  { name: 'Chelsea, NY', id: 9594 },
  { name: 'Chestnut Hill, MA', id: 9621 },
  { name: 'Dallas, TX', id: 9628 },
  { name: 'Denver, CO', id: 9766 },
  { name: 'Downtown Boston, MA', id: 9626 },
  { name: 'Dupont Circle, DC', id: 9629 },
  { name: 'East 64th, NY', id: 9598 },
  { name: 'East 86th, NY', id: 9865 },
  { name: 'FiDi, CA', id: 9613 },
  { name: 'Hoboken, NJ', id: 10132 },
  { name: 'Hollywood, CA', id: 9600 },
  { name: 'Houston, TX', id: 9649 },
  { name: 'Lincoln Park, IL', id: 9618 },
  { name: 'Long Beach, CA', id: 9652 },
  { name: 'Marina, CA', id: 9619 },
  { name: 'Minneapolis, MN', id: 9661 },
  { name: 'Nashville, TN', id: 9620 },
  { name: 'Navy Yard, DC', id: 10098 },
  { name: 'Newport Beach, CA', id: 10096 },
  { name: 'Noho, NY', id: 9604 },
  { name: 'Palo Alto, CA', id: 9642 },
  { name: 'Park Ave South, NY', id: 9601 },
  { name: 'Pasadena, CA', id: 9658 },
  { name: 'Philadelphia, PA', id: 9656 },
  { name: 'Portland, OR', id: 9898 },
  { name: 'River North, IL', id: 9608 },
  { name: 'Roslyn, NY', id: 9663 },
  { name: 'Salt Lake City, UT', id: 10165 },
  { name: 'San Diego, CA', id: 9599 },
  { name: 'Santa Monica, CA', id: 9668 },
  { name: 'Santana Row, CA', id: 9655 },
  { name: 'Scarsdale, NY', id: 9606 },
  { name: 'Scottsdale, AZ', id: 10063 },
  { name: 'Seattle, WA', id: 9643 },
  { name: 'South Bay, CA', id: 9633 },
  { name: 'Southampton, NY', id: 9610 },
  { name: 'Studio City, CA', id: 9674 },
  { name: 'Tampa, FL', id: 9964 },
  { name: 'Tribeca, NY', id: 9603 },
  { name: 'Upper West Side, NY', id: 9630 },
  { name: 'Venice, CA', id: 9609 },
  { name: 'Wainscott, NY', id: 9597 },
  { name: 'Wall Street, NY', id: 10233 },
  { name: 'West Hollywood, CA', id: 9595 },
  { name: 'Wicker Park, IL', id: 9662 },
  { name: 'Williamsburg, NY', id: 10133 },
  // Canada
  { name: 'Calgary', id: 9669 },
  { name: 'Vancouver', id: 9670 },
  { name: 'Richmond, Toronto', id: 9671 },
  { name: 'Yorkville, Toronto', id: 9672 },
  // UK
  { name: "King's Cross, London", id: 9832 },
  { name: 'Canary Wharf, London', id: 9837 },
  { name: 'London East', id: 9834 },
  { name: 'London Soho', id: 9840 },
  { name: 'London SW1', id: 9836 },
  { name: 'London West', id: 9835 },
  { name: "St. Paul's, London", id: 9833 },
  { name: 'Manchester', id: 9838 },
  { name: 'Liverpool', id: 9931 },
  // Ireland
  { name: 'Dublin', id: 10134 },
].sort((a, b) => a.name.localeCompare(b.name))

const APPROVERS = ['kaleigh@barrys.com', 'k.fiori@barrys.com', 'h.levin@barrys.com']

// Walks every page of an MT JSON:API list endpoint. Without this, a
// month-spanning class_sessions query (e.g. July 1-31 at a busy studio)
// returns only the first 100 results - and since MT defaults to recent-first,
// the user sees only the last few days of the range.
const fetchAllMtPages = async (
  basePath: string,
  maxPages = 30,
): Promise<{ data: any[]; included: any[] }> => {
  const allData: any[] = []
  const allIncluded: any[] = []
  let page = 1
  let totalPages = 1
  while (page <= totalPages && page <= maxPages) {
    const sep = basePath.includes('?') ? '&' : '?'
    const url = `${basePath}${sep}page=${page}&page_size=100`
    const r = await fetch(url)
    if (!r.ok) throw new Error(`MT error ${r.status}`)
    const body = await r.json()
    if (Array.isArray(body?.data)) allData.push(...body.data)
    if (Array.isArray(body?.included)) allIncluded.push(...body.included)
    totalPages = body?.meta?.pagination?.pages || 1
    page++
  }
  return { data: allData, included: allIncluded }
}

// Mirrors the backend mapping in mariana-tek-offers-api.js
const REWARD_OFFERS = [
  'Cancellation Forgiveness',
  'Complimentary Guest Pass',
  'Complimentary Shipping 1x Use',
  '25% Off Barry\'s Shop',
  'Birthday Credit',
]

interface MtInstructor {
  id: string
  name: string
}

interface MtClass {
  id: string
  class_name: string
  start_datetime: string
  end_datetime: string
  instructor_names: string[]
}

interface SelectedClass {
  mt_class_session_id: string
  class_name: string
  start_time: string
  instructor_name: string
}

interface ChallengeClassRow {
  id: number
  challenge_id: number
  mt_class_session_id: string
  class_name: string | null
  start_time: string | null
  instructor_name: string | null
  processed_at: string | null
  attendees_awarded: number | null
}

interface ChallengeTierRow {
  id: number
  challenge_id: number
  tier_order: number
  threshold: number
  bonus_points: number | null
  reward_offer_name: string | null
}

interface ChallengeRow {
  id: number
  studio_id: number
  studio_name: string
  bonus_points: number | null
  description: string | null
  status: 'active' | 'in_progress' | 'completed'
  processing_state?: 'active' | 'in_progress' | 'completed' | 'held'
  created_by_email: string
  created_by_name: string | null
  approved_by_email: string | null
  approved_at: string | null
  completed_at: string | null
  created_at: string
  classes: ChallengeClassRow[]
  // Phase 4 fields
  campaign_type?: 'attendance' | 'instructor' | 'tier' | null
  instructor_id?: string | null
  instructor_name?: string | null
  checkin_threshold?: number | null
  reward_offer_name?: string | null
  start_date?: string | null
  end_date?: string | null
  tiers?: ChallengeTierRow[]
}

interface TierDraft {
  threshold: string
  bonus_points: string
  reward_offer_name: string
}

interface RecommendationSlot {
  mt_class_session_id: string
  class_name: string
  start_datetime: string
  capacity: number | null
  studio_fill_pct: number | null
  studio_sessions: number | null
  network_fill_pct: number | null
  gap_pp: number | null
}

interface RecommendationStudio {
  studio_id: number
  studio_name: string
  slots: RecommendationSlot[]
}

interface RecommendationsResponse {
  generated_at?: string
  scope?: { studios_in_scope: number; lookback_days: number; upcoming_days: number }
  studios: RecommendationStudio[]
  error?: string
  warming?: boolean
  message?: string
}

const todayIso = () => new Date().toISOString().slice(0, 10)
const isoPlusDays = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

const studioTimeZone = (studioName?: string | null): string => {
  const lc = (studioName || '').toLowerCase()
  // International first (no US state suffix)
  if (lc.includes('london') || lc === 'manchester' || lc === 'liverpool') return 'Europe/London'
  if (lc === 'dublin') return 'Europe/Dublin'
  if (lc === 'vancouver') return 'America/Vancouver'
  if (lc === 'calgary') return 'America/Edmonton'
  if (lc.includes('toronto')) return 'America/Toronto'
  // US - match ", XX" state suffix
  const m = (studioName || '').match(/, ([A-Z]{2})$/)
  const state = m ? m[1] : ''
  if (['CA', 'OR', 'WA'].includes(state)) return 'America/Los_Angeles'
  if (state === 'AZ') return 'America/Phoenix'
  if (['CO', 'UT'].includes(state)) return 'America/Denver'
  if (['IL', 'MN', 'TX', 'TN'].includes(state)) return 'America/Chicago'
  return 'America/New_York'
}

// Peak / Off Peak per the studio's local clock:
//   Mon-Fri: 6:00-9:30 AM and 5:00-6:59 PM
//   Sat-Sun: 8:00-10:59 AM
const peakLabel = (iso?: string | null, tz?: string): 'Peak' | 'Off Peak' | null => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value || ''
  const wkday = get('weekday')
  const totalMins = parseInt(get('hour')) * 60 + parseInt(get('minute'))
  const weekend = wkday === 'Sat' || wkday === 'Sun'
  if (weekend) {
    return totalMins >= 8 * 60 && totalMins < 11 * 60 ? 'Peak' : 'Off Peak'
  }
  if (totalMins >= 6 * 60 && totalMins < 9 * 60 + 30) return 'Peak'
  if (totalMins >= 17 * 60 && totalMins < 19 * 60) return 'Peak'
  return 'Off Peak'
}

const PeakBadge: React.FC<{ iso?: string | null; tz?: string }> = ({ iso, tz }) => {
  const label = peakLabel(iso, tz)
  if (!label) return null
  const peak = label === 'Peak'
  return (
    <span style={{
      marginLeft: '0.4rem',
      padding: '0.05rem 0.45rem',
      borderRadius: 999,
      fontSize: '0.8rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: peak ? '#d70036' : '#e9e9e9',
      color: peak ? 'white' : '#555',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  )
}

const fmtDateTimeInTz = (iso?: string | null, tz?: string) => {
  if (!iso) return '-'
  return new Date(iso).toLocaleString('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  } as any)
}
const localDateKeyInTz = (iso?: string | null, tz?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (type: string) => parts.find((part) => part.type === type)?.value || ''
  return `${get('year')}-${get('month')}-${get('day')}`
}
const fmtDateOnly = (s?: string | null) => {
  if (!s) return '-'
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!match) return '-'
  const [, year, month, day] = match
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toLocaleDateString('en-US', {
    dateStyle: 'medium' as any,
    timeZone: 'UTC',
  })
}
const compareIsoChronologically = (left?: string | null, right?: string | null) => {
  const leftMs = Date.parse(left || '')
  const rightMs = Date.parse(right || '')
  if (Number.isFinite(leftMs) && Number.isFinite(rightMs) && leftMs !== rightMs) {
    return leftMs - rightMs
  }
  return (left || '').localeCompare(right || '')
}
const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('en-US', { dateStyle: 'medium' as any }) : '-'

const StudioChallengesButton: React.FC = () => {
  const { isAdmin, user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [tab, setTab] = useState<'active' | 'in_progress' | 'completed' | 'create' | 'recommendations'>('active')

  const isApprover = APPROVERS.includes((user?.email || '').toLowerCase())

  // List state per tab
  const [challenges, setChallenges] = useState<ChallengeRow[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  // Create wizard state
  const [step, setStep] = useState<1 | 2>(1)
  const [campaignType, setCampaignType] = useState<'attendance' | 'instructor' | 'tier'>('attendance')
  const [studioId, setStudioId] = useState<number | null>(null)
  const [studioName, setStudioName] = useState('')
  const [scheduleStart, setScheduleStart] = useState(todayIso())
  const [scheduleEnd, setScheduleEnd] = useState(isoPlusDays(7))
  const [scheduleClasses, setScheduleClasses] = useState<MtClass[]>([])
  const [loadingSchedule, setLoadingSchedule] = useState(false)
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [pendingChecks, setPendingChecks] = useState<Set<string>>(new Set())
  const [picked, setPicked] = useState<SelectedClass[]>([])
  const [bonusPoints, setBonusPoints] = useState('')
  const [rewardOfferName, setRewardOfferName] = useState('')
  const [description, setDescription] = useState('')
  // instructor-specific
  const [startDate, setStartDate] = useState(todayIso())
  const [endDate, setEndDate] = useState(isoPlusDays(14))
  const [instructors, setInstructors] = useState<MtInstructor[]>([])
  const [loadingInstructors, setLoadingInstructors] = useState(false)
  const [instructorsError, setInstructorsError] = useState<string | null>(null)
  const [instructorId, setInstructorId] = useState('')
  const [instructorName, setInstructorName] = useState('')
  const [checkinThreshold, setCheckinThreshold] = useState('')
  // tier-specific
  const [tiers, setTiers] = useState<TierDraft[]>([{ threshold: '', bonus_points: '', reward_offer_name: '' }])
  // recommendations tab
  const [recommendations, setRecommendations] = useState<RecommendationsResponse | null>(null)
  const [loadingRecommendations, setLoadingRecommendations] = useState(false)
  const [recommendationsError, setRecommendationsError] = useState<string | null>(null)
  const [checkedRecs, setCheckedRecs] = useState<Set<string>>(new Set())

  const [submitting, setSubmitting] = useState(false)
  const submissionKey = useRef<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
  const [approvingId, setApprovingId] = useState<number | null>(null)
  const [rejectingId, setRejectingId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectSubmittingId, setRejectSubmittingId] = useState<number | null>(null)

  const resetCreateState = () => {
    setStep(1)
    setCampaignType('attendance')
    setStudioId(null)
    setStudioName('')
    setScheduleStart(todayIso())
    setScheduleEnd(isoPlusDays(7))
    setScheduleClasses([])
    setScheduleError(null)
    setPendingChecks(new Set())
    setPicked([])
    setBonusPoints('')
    setRewardOfferName('')
    setDescription('')
    setStartDate(todayIso())
    setEndDate(isoPlusDays(14))
    setInstructors([])
    setInstructorsError(null)
    setInstructorId('')
    setInstructorName('')
    setCheckinThreshold('')
    setTiers([{ threshold: '', bonus_points: '', reward_offer_name: '' }])
    setSubmitError(null)
    setSubmitSuccess(null)
    submissionKey.current = null
  }

  const loadChallenges = async (status: 'active' | 'in_progress' | 'completed') => {
    setLoadingList(true)
    setListError(null)
    try {
      const r = await fetch(`/api/studio-challenges?status=${status}`)
      if (!r.ok) throw new Error(`Failed to load: ${r.status}`)
      const data = await r.json()
      setChallenges(data.challenges || [])
    } catch (e: any) {
      setListError(e.message || 'Failed to load')
    } finally {
      setLoadingList(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    if ((tab === 'create' || tab === 'recommendations') && !isAdmin) {
      setTab('active')
      return
    }
    if (tab === 'create' || tab === 'recommendations') return
    loadChallenges(tab)
  }, [isAdmin, isOpen, tab])

  const loadRecommendations = async () => {
    setLoadingRecommendations(true)
    setRecommendationsError(null)
    try {
      const r = await fetch('/api/studio-challenges/recommendations')
      if (!r.ok) throw new Error(`Failed to load: ${r.status}`)
      const data: RecommendationsResponse = await r.json()
      if (data.error) throw new Error(data.error === 'snowflake_not_configured' ? 'Snowflake is not configured on the server.' : data.error)
      setRecommendations(data)
    } catch (e: any) {
      setRecommendationsError(e.message || 'Failed to load recommendations')
    } finally {
      setLoadingRecommendations(false)
    }
  }

  useEffect(() => {
    if (isOpen && tab === 'recommendations' && !recommendations && !loadingRecommendations) {
      loadRecommendations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab])

  // Auto-poll while the server is warming the cache.
  useEffect(() => {
    if (!isOpen) return
    if (tab !== 'recommendations') return
    if (!recommendations?.warming) return
    const t = setTimeout(() => loadRecommendations(), 10000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations, isOpen, tab])

  const toggleRec = (id: string) => {
    setCheckedRecs((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // "Add Selected to Create" for one studio's checked recommendations.
  // Replaces any in-progress Create state with the picked classes for this studio
  // and switches to the Create tab on the Attendance flow.
  const addStudioRecsToCreate = (studio: RecommendationStudio) => {
    const slotsToAdd = studio.slots.filter((s) => checkedRecs.has(s.mt_class_session_id))
    if (slotsToAdd.length === 0) return
    if (picked.length > 0 && !window.confirm('This will replace your current Create selections. Continue?')) return

    setCampaignType('attendance')
    setStudioId(studio.studio_id)
    setStudioName(studio.studio_name)
    const additions: SelectedClass[] = slotsToAdd.map((s) => ({
      mt_class_session_id: s.mt_class_session_id,
      class_name: s.class_name,
      start_time: s.start_datetime,
      instructor_name: '',
    }))
    setPicked(additions.sort((a, b) => a.start_time.localeCompare(b.start_time)))
    setPendingChecks(new Set())
    setCheckedRecs(new Set())
    setStep(1)
    setTab('create')
  }

  const fetchSchedule = async () => {
    if (!studioId || !scheduleStart || !scheduleEnd) return
    setLoadingSchedule(true)
    setScheduleError(null)
    setPendingChecks(new Set())
    try {
      const basePath = `/api/marianatek/api/class_sessions/?location=${studioId}&min_date=${scheduleStart}&max_date=${scheduleEnd}&include=location,instructors`
      const body = await fetchAllMtPages(basePath)
      const includedMap: Record<string, any> = {}
      for (const i of body.included || []) includedMap[`${i.type}/${i.id}`] = i
      const mapped: MtClass[] = (body.data || []).map((s: any) => {
        const a = s.attributes || {}
        const rel = s.relationships || {}
        const instructorIds = (rel.instructors?.data || []).map((d: any) => d.id)
        const instructorNames = instructorIds
          .map((id: string) => includedMap[`users/${id}`]?.attributes?.full_name)
          .filter(Boolean)
        return {
          id: s.id,
          class_name: a.class_type_display || 'Class',
          start_datetime: a.start_datetime,
          end_datetime: a.end_datetime,
          instructor_names: instructorNames.length ? instructorNames : (a.instructor_names || []),
        }
      })
      mapped.sort((x, y) => compareIsoChronologically(x.start_datetime, y.start_datetime))
      setScheduleClasses(mapped)
    } catch (e: any) {
      setScheduleError(e.message || 'Failed to load schedule')
      setScheduleClasses([])
    } finally {
      setLoadingSchedule(false)
    }
  }

  useEffect(() => {
    if (tab === 'create' && studioId && scheduleStart && scheduleEnd) fetchSchedule()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioId, scheduleStart, scheduleEnd, tab])

  const togglePending = (id: string) => {
    setPendingChecks((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const addCheckedToList = () => {
    const existing = new Set(picked.map((p) => p.mt_class_session_id))
    const additions: SelectedClass[] = []
    for (const cls of scheduleClasses) {
      if (!pendingChecks.has(cls.id) || existing.has(cls.id)) continue
      additions.push({
        mt_class_session_id: cls.id,
        class_name: cls.class_name,
        start_time: cls.start_datetime,
        instructor_name: (cls.instructor_names || []).join(', '),
      })
    }
    if (additions.length) {
      setPicked((prev) => [...prev, ...additions].sort((a, b) => a.start_time.localeCompare(b.start_time)))
    }
    setPendingChecks(new Set())
  }

  const removePicked = (id: string) => {
    setPicked((prev) => prev.filter((p) => p.mt_class_session_id !== id))
  }

  const fetchInstructors = async () => {
    if (!studioId || !startDate || !endDate) return
    setLoadingInstructors(true)
    setInstructorsError(null)
    try {
      // Mirror class-lookup-api.js's proven include pattern so users records
      // are pulled in alongside instructors.
      const basePath = `/api/marianatek/api/class_sessions/?location=${studioId}&min_date=${startDate}&max_date=${endDate}&include=location,instructors,employee_public_profiles`
      const body = await fetchAllMtPages(basePath)

      // MT shape: relationships.instructors.data[] -> {type:'employees', id}.
      // Visible name lives on employee_public_profiles[*].attributes.schedule_display_name,
      // linked back via relationships.employee.data.id. Build employeeId -> name map.
      const nameByEmployeeId: Record<string, string> = {}
      for (const i of body.included || []) {
        if (i.type === 'employee_public_profiles') {
          const empId = i.relationships?.employee?.data?.id
          const display = i.attributes?.schedule_display_name
          if (empId && display) nameByEmployeeId[String(empId)] = display
        }
      }

      const seen = new Map<string, string>()
      for (const s of body.data || []) {
        const rel = s.relationships || {}
        const instructorRefs = (rel.instructors?.data || []) as Array<{ type?: string; id: string }>
        for (const ref of instructorRefs) {
          const id = String(ref.id)
          if (seen.has(id)) continue
          const name = nameByEmployeeId[id] || `Instructor #${id}`
          seen.set(id, name)
        }
      }

      const list: MtInstructor[] = Array.from(seen.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
      setInstructors(list)
      if (instructorId && !seen.has(instructorId)) {
        setInstructorId('')
        setInstructorName('')
      }
    } catch (e: any) {
      setInstructorsError(e.message || 'Failed to load instructors')
      setInstructors([])
    } finally {
      setLoadingInstructors(false)
    }
  }

  useEffect(() => {
    if (tab === 'create' && campaignType === 'instructor' && studioId && startDate && endDate) {
      fetchInstructors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, campaignType, studioId, startDate, endDate])

  const submitRequest = async () => {
    setSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      const points = bonusPoints ? parseInt(bonusPoints) : null
      const hasPoints = points != null && !isNaN(points) && points > 0
      const hasOffer = !!rewardOfferName
      // Tier campaigns set rewards per-tier - skip the top-level check.
      if (campaignType !== 'tier' && !hasPoints && !hasOffer) {
        throw new Error('Set at least one reward (points or offer)')
      }

      const common = {
        studio_id: studioId,
        studio_name: studioName,
        bonus_points: hasPoints ? points : null,
        reward_offer_name: hasOffer ? rewardOfferName : null,
        description: description || null,
        created_by_email: user?.email || '',
        created_by_name: user?.name || user?.preferred_username || '',
        campaign_type: campaignType,
      }

      let body: any
      if (campaignType === 'attendance') {
        if (!studioId || !studioName || !scheduleStart || !scheduleEnd || picked.length === 0) {
          throw new Error('Pick a studio and at least one class')
        }
        body = {
          ...common,
          classes: picked,
          window_start: scheduleStart,
          window_end: scheduleEnd,
        }
      } else if (campaignType === 'instructor') {
        if (!studioId || !studioName || !instructorId || !checkinThreshold || !startDate || !endDate) {
          throw new Error('Fill in studio, instructor, threshold, start and end dates')
        }
        body = {
          ...common,
          instructor_id: instructorId,
          instructor_name: instructorName,
          checkin_threshold: parseInt(checkinThreshold),
          start_date: startDate,
          end_date: endDate,
        }
      } else {
        // tier
        if (!studioId || !studioName || !scheduleStart || !scheduleEnd || picked.length === 0) {
          throw new Error('Pick a studio and at least one qualifying class')
        }
        if (tiers.length === 0) throw new Error('Add at least one tier')
        let last = 0
        const cleanedTiers: any[] = []
        for (let i = 0; i < tiers.length; i++) {
          const t = tiers[i]
          const th = parseInt(t.threshold)
          const tp = t.bonus_points ? parseInt(t.bonus_points) : null
          const tHasPoints = tp != null && !isNaN(tp) && tp > 0
          const tHasOffer = !!t.reward_offer_name
          if (isNaN(th) || th <= 0) throw new Error(`Tier ${i + 1}: threshold must be a positive integer`)
          if (th <= last) throw new Error(`Tier ${i + 1}: threshold must be greater than the previous tier's`)
          if (!tHasPoints && !tHasOffer) throw new Error(`Tier ${i + 1}: set at least one reward (points or offer)`)
          cleanedTiers.push({
            threshold: th,
            bonus_points: tHasPoints ? tp : null,
            reward_offer_name: tHasOffer ? t.reward_offer_name : null,
          })
          last = th
        }
        // For tier campaigns the top-level bonus_points/reward_offer_name aren't used.
        body = {
          ...common,
          bonus_points: null,
          reward_offer_name: null,
          classes: picked,
          window_start: scheduleStart,
          window_end: scheduleEnd,
          tiers: cleanedTiers,
        }
      }

      if (!submissionKey.current) submissionKey.current = window.crypto.randomUUID()
      const r = await fetch('/api/studio-challenges', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': submissionKey.current,
        },
        body: JSON.stringify(body),
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || `Submit failed: ${r.status}`)
      setSubmitSuccess(`Submitted! Request #${data.challenge.id} sent to approvers.`)
      resetCreateState()
      setTab('active')
    } catch (e: any) {
      setSubmitError(e.message || 'Submit failed')
    } finally {
      setSubmitting(false)
    }
  }

  const approveChallenge = async (id: number) => {
    setApprovingId(id)
    try {
      const r = await fetch(`/api/studio-challenges/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approver_email: user?.email }),
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || `Approve failed: ${r.status}`)
      await loadChallenges('active')
    } catch (e: any) {
      alert(e.message || 'Approve failed')
    } finally {
      setApprovingId(null)
    }
  }

  const startReject = (id: number) => {
    setRejectingId(id)
    setRejectReason('')
  }
  const cancelReject = () => {
    setRejectingId(null)
    setRejectReason('')
  }
  const sendReject = async (id: number) => {
    if (!rejectReason.trim()) return
    setRejectSubmittingId(id)
    try {
      const r = await fetch(`/api/studio-challenges/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approver_email: user?.email, reason: rejectReason.trim() }),
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || `Reject failed: ${r.status}`)
      cancelReject()
      await loadChallenges('active')
    } catch (e: any) {
      alert(e.message || 'Reject failed')
    } finally {
      setRejectSubmittingId(null)
    }
  }

  const renderRewardSummary = (c: ChallengeRow) => {
    const parts: string[] = []
    if (c.bonus_points) parts.push(`${c.bonus_points} pts`)
    if (c.reward_offer_name) parts.push(`Offer: ${c.reward_offer_name}`)
    return parts.join(' + ') || '-'
  }

  const renderListTab = (status: 'active' | 'in_progress' | 'completed') => {
    if (loadingList) return <Empty>Loading…</Empty>
    if (listError) return <ErrorBanner>{listError}</ErrorBanner>
    if (!challenges.length) return <Empty>No {status.replace('_', ' ')} challenges</Empty>
    return (
      <div>
        {challenges.map((c) => {
          const isInstructor = c.campaign_type === 'instructor'
          const isTier = c.campaign_type === 'tier'
          const isHeld = c.processing_state === 'held'
          const typeLabel = isInstructor ? 'Instructor' : isTier ? 'Tier' : 'Attendance'
          const typeColor = isInstructor ? '#5a3aa8' : isTier ? '#a8643a' : '#3a78a8'
          const headerReward = isTier
            ? `${(c.tiers || []).length} tiers`
            : renderRewardSummary(c)
          return (
            <ChallengeCard key={c.id}>
              <CardHeader>
                <div>
                  <CardTitle>
                    {c.studio_name} - {headerReward}
                    <span style={{
                      marginLeft: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: 'white',
                      background: typeColor,
                      padding: '0.15rem 0.5rem',
                      borderRadius: 4,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>
                      {typeLabel}
                    </span>
                  </CardTitle>
                  <CardMeta>
                    Submitted by {c.created_by_name || c.created_by_email} on {fmtDate(c.created_at)}
                    {c.approved_by_email && <> · Approved by {c.approved_by_email} on {fmtDate(c.approved_at)}</>}
                  </CardMeta>
                </div>
                <StatusPill status={isHeld ? 'held' : c.status}>
                  {isHeld ? 'Held' : c.status.replace('_', ' ')}
                </StatusPill>
              </CardHeader>
              {isHeld && (
                <HeldBanner>
                  Paused for reward reconciliation. Automatic processing is off for this legacy campaign.
                </HeldBanner>
              )}
              {c.description && <div style={{ fontSize: '1.05rem', margin: '0.5rem 0' }}>{c.description}</div>}
              {isInstructor ? (
                <div style={{ fontSize: '1.05rem', marginTop: '0.5rem', lineHeight: 1.6 }}>
                  <div><strong>Instructor:</strong> {c.instructor_name}</div>
                  <div><strong>Check-ins to qualify:</strong> {c.checkin_threshold}</div>
                  <div><strong>Window:</strong> {c.start_date} → {c.end_date}</div>
                </div>
              ) : isTier ? (
                <>
                  <div style={{ fontSize: '1.05rem', marginTop: '0.5rem' }}>
                    <strong>Tiers:</strong>
                    <ul style={{ margin: '0.2rem 0 0.5rem 1rem', padding: 0 }}>
                      {(c.tiers || []).map((t) => {
                        const reward = [t.bonus_points ? `${t.bonus_points} pts` : null, t.reward_offer_name ? `Offer: ${t.reward_offer_name}` : null].filter(Boolean).join(' + ') || '-'
                        return <li key={t.id}>Tier {t.tier_order}: ≥ {t.threshold} → {reward}</li>
                      })}
                    </ul>
                  </div>
                  <div style={{ fontSize: '0.95rem', color: theme.color.text.grey, marginBottom: '0.25rem' }}>
                    Qualifying classes ({c.classes.length}):
                  </div>
                  <ClassList>
                    {c.classes.map((k) => (
                      <li key={k.id} style={{ padding: '0.2rem 0' }}>
                        {fmtDateTimeInTz(k.start_time, studioTimeZone(c.studio_name))}
                        <PeakBadge iso={k.start_time} tz={studioTimeZone(c.studio_name)} />
                        {' - '}{k.class_name || 'Class'}
                        {k.instructor_name ? ` · ${k.instructor_name}` : ''}
                        {' · '}<code style={{ fontSize: '0.9rem' }}>id {k.mt_class_session_id}</code>
                        {k.processed_at && (
                          <span style={{ marginLeft: '0.5rem', color: '#086e2a', fontSize: '0.9rem' }}>✓ closed</span>
                        )}
                      </li>
                    ))}
                  </ClassList>
                </>
              ) : (
                <ClassList>
                  {c.classes.map((k) => (
                    <li key={k.id} style={{ padding: '0.2rem 0' }}>
                      {fmtDateTimeInTz(k.start_time, studioTimeZone(c.studio_name))}
                      <PeakBadge iso={k.start_time} tz={studioTimeZone(c.studio_name)} />
                      {' - '}{k.class_name || 'Class'}
                      {k.instructor_name ? ` · ${k.instructor_name}` : ''}
                      {' · '}<code style={{ fontSize: '0.9rem' }}>id {k.mt_class_session_id}</code>
                      {k.processed_at && (
                        <span style={{ marginLeft: '0.5rem', color: '#086e2a', fontSize: '0.9rem' }}>
                          ✓ {k.attendees_awarded || 0} awarded
                        </span>
                      )}
                    </li>
                  ))}
                </ClassList>
              )}
              {status === 'active' && isApprover && rejectingId !== c.id && (
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <SecondaryButton
                    onClick={() => startReject(c.id)}
                    style={{ borderColor: '#d70036', color: '#d70036' }}
                  >
                    Reject
                  </SecondaryButton>
                  <PrimaryButton onClick={() => approveChallenge(c.id)} disabled={approvingId === c.id}>
                    {approvingId === c.id ? 'Approving…' : 'Approve'}
                  </PrimaryButton>
                </div>
              )}
              {status === 'active' && isApprover && rejectingId === c.id && (
                <div style={{ marginTop: '0.75rem' }}>
                  <Label>Reason for rejection *</Label>
                  <TextArea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Why is this request being rejected? The requester and the approvers will receive this in the rejection email."
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <SecondaryButton onClick={cancelReject} disabled={rejectSubmittingId === c.id}>
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton
                      onClick={() => sendReject(c.id)}
                      disabled={!rejectReason.trim() || rejectSubmittingId === c.id}
                    >
                      {rejectSubmittingId === c.id ? 'Sending…' : 'Send'}
                    </PrimaryButton>
                  </div>
                </div>
              )}
              {status === 'active' && !isApprover && (
                <CardMeta style={{ marginTop: '0.5rem' }}>
                  Approval restricted to: {APPROVERS.join(', ')}
                </CardMeta>
              )}
            </ChallengeCard>
          )
        })}
      </div>
    )
  }

  const renderStudioPicker = (onChangeWarn?: () => boolean) => (
    <div style={{ flex: 1 }}>
      <Label>Studio (US, no Miami)</Label>
      <Select
        value={studioId ?? ''}
        onChange={(e) => {
          if (onChangeWarn && !onChangeWarn()) return
          const id = parseInt(e.target.value)
          const studio = ALL_STUDIOS.find((s) => s.id === id)
          setStudioId(id || null)
          setStudioName(studio?.name || '')
        }}
      >
        <option value="">- Choose a studio -</option>
        {ALL_STUDIOS.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </Select>
    </div>
  )

  const renderCampaignTypeRadio = () => (
    <div style={{ marginBottom: '0.75rem' }}>
      <Label>Campaign Type</Label>
      <div style={{ display: 'flex', gap: '1.5rem', fontSize: '1.1rem', flexWrap: 'wrap' }}>
        <label style={{ cursor: 'pointer' }}>
          <input
            type="radio"
            name="campaign_type"
            checked={campaignType === 'attendance'}
            onChange={() => {
              if ((picked.length > 0 || instructorId) && !window.confirm('Switching campaign type will clear your selections. Continue?')) return
              setCampaignType('attendance')
              setInstructorId('')
              setInstructorName('')
              setCheckinThreshold('')
            }}
            style={{ marginRight: '0.4rem' }}
          />
          Attendance (specific classes)
        </label>
        <label style={{ cursor: 'pointer' }}>
          <input
            type="radio"
            name="campaign_type"
            checked={campaignType === 'instructor'}
            onChange={() => {
              if (picked.length > 0 && !window.confirm('Switching campaign type will clear your selections. Continue?')) return
              setCampaignType('instructor')
              setPicked([])
              setPendingChecks(new Set())
            }}
            style={{ marginRight: '0.4rem' }}
          />
          Instructor (track check-ins with one instructor)
        </label>
        <label style={{ cursor: 'pointer' }}>
          <input
            type="radio"
            name="campaign_type"
            checked={campaignType === 'tier'}
            onChange={() => {
              if (instructorId && !window.confirm('Switching campaign type will clear your instructor selection. Continue?')) return
              setCampaignType('tier')
              setInstructorId('')
              setInstructorName('')
              setCheckinThreshold('')
            }}
            style={{ marginRight: '0.4rem' }}
          />
          Tier (highest reached pays out)
        </label>
      </div>
    </div>
  )

  const renderCreateTab = () => {
    if (step === 1) {
      if (campaignType === 'instructor') {
        return (
          <div>
            {submitSuccess && <SuccessBanner>{submitSuccess}</SuccessBanner>}
            {submitError && <ErrorBanner>{submitError}</ErrorBanner>}
            {renderCampaignTypeRadio()}
            <Row>
              {renderStudioPicker(() => {
                if (!instructorId) return true
                return window.confirm('Changing studio will clear the instructor selection. Continue?')
              })}
              <div>
                <Label>Start date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </Row>

            {!studioId && <Empty>Pick a studio to load its instructors.</Empty>}
            {studioId && loadingInstructors && <Empty>Loading instructors…</Empty>}
            {studioId && instructorsError && <ErrorBanner>{instructorsError}</ErrorBanner>}
            {studioId && !loadingInstructors && !instructorsError && instructors.length === 0 && (
              <Empty>No instructors found at this studio for {startDate} → {endDate}.</Empty>
            )}
            {studioId && !loadingInstructors && instructors.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <Label>Instructor to track</Label>
                <Select
                  value={instructorId}
                  onChange={(e) => {
                    const id = e.target.value
                    setInstructorId(id)
                    const found = instructors.find((i) => i.id === id)
                    setInstructorName(found?.name || '')
                  }}
                >
                  <option value="">- Choose an instructor -</option>
                  {instructors.map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </Select>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <PrimaryButton
                onClick={() => setStep(2)}
                disabled={!studioId || !instructorId || !startDate || !endDate}
              >
                Next →
              </PrimaryButton>
            </div>
          </div>
        )
      }

      // attendance step 1
      return (
        <div>
          {submitSuccess && <SuccessBanner>{submitSuccess}</SuccessBanner>}
          {submitError && <ErrorBanner>{submitError}</ErrorBanner>}
          {renderCampaignTypeRadio()}

          <Row>
            {renderStudioPicker(() => {
              if (picked.length === 0) return true
              const ok = window.confirm('Changing studio will clear your selected classes. Continue?')
              if (ok) setPicked([])
              return ok
            })}
            <div>
              <Label htmlFor="studio-challenge-schedule-start">From</Label>
              <Input
                id="studio-challenge-schedule-start"
                type="date"
                value={scheduleStart}
                onChange={(e) => setScheduleStart(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="studio-challenge-schedule-end">To</Label>
              <Input
                id="studio-challenge-schedule-end"
                type="date"
                value={scheduleEnd}
                onChange={(e) => setScheduleEnd(e.target.value)}
              />
            </div>
          </Row>

          {!studioId && <Empty>Pick a studio to load its schedule.</Empty>}

          {studioId && loadingSchedule && <Empty>Loading classes…</Empty>}
          {studioId && scheduleError && <ErrorBanner>{scheduleError}</ErrorBanner>}
          {studioId && !loadingSchedule && !scheduleError && scheduleClasses.length === 0 && (
            <Empty>No classes scheduled between {fmtDateOnly(scheduleStart)} and {fmtDateOnly(scheduleEnd)}.</Empty>
          )}

          {studioId && !loadingSchedule && scheduleClasses.length > 0 && (() => {
            // Group classes by their local YYYY-MM-DD so the user can scan multi-week ranges.
            const groups = new Map<string, MtClass[]>()
            const timeZone = studioTimeZone(studioName)
            for (const c of scheduleClasses) {
              const key = localDateKeyInTz(c.start_datetime, timeZone)
              const arr = groups.get(key) || []
              arr.push(c)
              groups.set(key, arr)
            }
            const orderedKeys = Array.from(groups.keys()).sort()
            return (
              <>
                <ScheduleList>
                  {orderedKeys.map((dateKey) => (
                    <React.Fragment key={dateKey}>
                      <li style={{
                        listStyle: 'none',
                        background: '#f0f0f0',
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: theme.color.text.grey,
                      }}>
                        {fmtDateOnly(dateKey)}
                      </li>
                      {(groups.get(dateKey) || []).map((c) => {
                        const alreadyAdded = picked.some((p) => p.mt_class_session_id === c.id)
                        return (
                          <ClassRow key={c.id} checked={pendingChecks.has(c.id)}>
                            <input
                              type="checkbox"
                              checked={alreadyAdded || pendingChecks.has(c.id)}
                              disabled={alreadyAdded}
                              onChange={() => togglePending(c.id)}
                            />
                            <span style={{ flex: 1 }}>
                              <strong>{fmtDateTimeInTz(c.start_datetime, studioTimeZone(studioName))}</strong>
                              <PeakBadge iso={c.start_datetime} tz={studioTimeZone(studioName)} />
                              {' - '}{c.class_name}
                              {c.instructor_names.length > 0 && <> · {c.instructor_names.join(', ')}</>}
                              {alreadyAdded && (
                                <span style={{ marginLeft: '0.5rem', color: '#086e2a', fontSize: '0.9rem' }}>
                                  (added)
                                </span>
                              )}
                            </span>
                          </ClassRow>
                        )
                      })}
                    </React.Fragment>
                  ))}
                </ScheduleList>

                {pendingChecks.size > 0 && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <PrimaryButton onClick={addCheckedToList}>
                      Add to Challenge List ({pendingChecks.size})
                    </PrimaryButton>
                  </div>
                )}
              </>
            )
          })()}

          <RunningListBox>
            <Label>Selected Classes ({picked.length})</Label>
            {picked.length === 0 ? (
              <div style={{ fontSize: '1.05rem', color: theme.color.text.grey }}>
                No classes added yet. Pick a date range, check the classes you want (across multiple days if needed), and click "Add to Challenge List".
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {picked.map((p) => (
                  <li key={p.mt_class_session_id} style={{ padding: '0.35rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                    <span>
                      {fmtDateTimeInTz(p.start_time, studioTimeZone(studioName))}
                      <PeakBadge iso={p.start_time} tz={studioTimeZone(studioName)} />
                      {' - '}{p.class_name}
                      {p.instructor_name ? ` · ${p.instructor_name}` : ''}
                      {' · '}<code style={{ fontSize: '0.9rem' }}>id {p.mt_class_session_id}</code>
                    </span>
                    <SecondaryButton onClick={() => removePicked(p.mt_class_session_id)}>Remove</SecondaryButton>
                  </li>
                ))}
              </ul>
            )}
          </RunningListBox>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <PrimaryButton onClick={() => setStep(2)} disabled={picked.length === 0}>
              Next →
            </PrimaryButton>
          </div>
        </div>
      )
    }

    // Step 2 - branches by campaign type.
    const isInstructor = campaignType === 'instructor'
    const isTier = campaignType === 'tier'
    const points = bonusPoints ? parseInt(bonusPoints) : NaN
    const hasValidPoints = !isNaN(points) && points > 0
    const hasOffer = !!rewardOfferName

    // For tier - validate the tiers list as the user types.
    let tierError: string | null = null
    if (isTier) {
      let last = 0
      for (let i = 0; i < tiers.length; i++) {
        const t = tiers[i]
        const th = parseInt(t.threshold)
        const tp = t.bonus_points ? parseInt(t.bonus_points) : null
        const tHasPoints = tp != null && !isNaN(tp) && tp > 0
        const tHasOffer = !!t.reward_offer_name
        if (!t.threshold) { tierError = `Tier ${i + 1}: enter a threshold`; break }
        if (isNaN(th) || th <= 0) { tierError = `Tier ${i + 1}: threshold must be a positive integer`; break }
        if (th <= last) { tierError = `Tier ${i + 1}: threshold must be greater than ${last}`; break }
        if (!tHasPoints && !tHasOffer) { tierError = `Tier ${i + 1}: set at least one reward (points or offer)`; break }
        last = th
      }
    }

    const canSubmit = isTier
      ? (picked.length > 0 && tiers.length > 0 && !tierError)
      : (hasValidPoints || hasOffer) && (
          isInstructor
            ? !!(instructorId && checkinThreshold && parseInt(checkinThreshold) > 0)
            : picked.length > 0
        )

    const updateTier = (i: number, field: keyof TierDraft, value: string) => {
      setTiers((prev) => prev.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)))
    }
    const addTier = () => setTiers((prev) => [...prev, { threshold: '', bonus_points: '', reward_offer_name: '' }])
    const removeTier = (i: number) => {
      setTiers((prev) => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i))
    }

    return (
      <div>
        {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

        {isTier && (
          <>
            <Label>Tier Award Structure</Label>
            <div style={{ fontSize: '0.95rem', color: theme.color.text.grey, marginBottom: '0.5rem' }}>
              Each tier defines how many of the qualifying classes a client must check in to and what they earn.
              Clients receive only the highest tier they reach. Thresholds must increase between tiers.
            </div>
            {tiers.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.05rem', fontWeight: 600, minWidth: '3rem' }}>Tier {i + 1}</span>
                <Input
                  type="number"
                  min={1}
                  value={t.threshold}
                  onChange={(e) => updateTier(i, 'threshold', e.target.value)}
                  placeholder="check-ins"
                  style={{ width: '120px' }}
                />
                <Input
                  type="number"
                  min={0}
                  value={t.bonus_points}
                  onChange={(e) => updateTier(i, 'bonus_points', e.target.value)}
                  placeholder="points"
                  style={{ width: '110px' }}
                />
                <Select
                  value={t.reward_offer_name}
                  onChange={(e) => updateTier(i, 'reward_offer_name', e.target.value)}
                  style={{ minWidth: '200px' }}
                >
                  <option value="">- No offer -</option>
                  {REWARD_OFFERS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </Select>
                <SecondaryButton
                  onClick={() => removeTier(i)}
                  disabled={tiers.length === 1}
                  style={{ padding: '0.4rem 0.75rem' }}
                >
                  ✕
                </SecondaryButton>
              </div>
            ))}
            <SecondaryButton onClick={addTier} style={{ marginBottom: '1rem' }}>+ Add Tier</SecondaryButton>
            {tierError && <ErrorBanner>{tierError}</ErrorBanner>}
          </>
        )}

        {isInstructor && (
          <>
            <Label>Check-ins required to qualify</Label>
            <Input
              type="number"
              min={1}
              value={checkinThreshold}
              onChange={(e) => setCheckinThreshold(e.target.value)}
              placeholder="e.g. 5"
              style={{ width: '180px', marginBottom: '1rem' }}
            />
          </>
        )}

        {!isTier && (
          <>
            <Label>Bonus points (optional if offer is set)</Label>
            <Input
              type="number"
              min={1}
              value={bonusPoints}
              onChange={(e) => setBonusPoints(e.target.value)}
              placeholder="e.g. 100"
              style={{ width: '180px', marginBottom: '1rem' }}
            />

            <Label>Reward offer (optional if points are set)</Label>
            <Select
              value={rewardOfferName}
              onChange={(e) => setRewardOfferName(e.target.value)}
              style={{ marginBottom: '1rem' }}
            >
              <option value="">- No offer -</option>
              {REWARD_OFFERS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </Select>
          </>
        )}

        <Label>Description (optional)</Label>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Reason / context for the approvers"
        />

        <RunningListBox>
          <Label>Summary</Label>
          <div style={{ fontSize: '1.05rem' }}>
            <div><strong>Type:</strong> {isTier ? 'Tier' : isInstructor ? 'Instructor' : 'Attendance'}</div>
            <div><strong>Studio:</strong> {studioName}</div>
            {isInstructor ? (
              <>
                <div><strong>Instructor:</strong> {instructorName}</div>
                <div><strong>Check-ins to qualify:</strong> {checkinThreshold || '-'}</div>
                <div><strong>Window:</strong> {startDate} → {endDate}</div>
              </>
            ) : (
              <div><strong>Classes:</strong> {picked.length}</div>
            )}
            {isTier ? (
              <div style={{ marginTop: '0.4rem' }}>
                <strong>Tiers:</strong>
                <ul style={{ margin: '0.2rem 0 0 1rem', padding: 0 }}>
                  {tiers.map((t, i) => {
                    const tp = t.bonus_points ? parseInt(t.bonus_points) : null
                    const reward = [tp ? `${tp} pts` : null, t.reward_offer_name ? `Offer: ${t.reward_offer_name}` : null].filter(Boolean).join(' + ') || '-'
                    return <li key={i}>Tier {i + 1}: ≥ {t.threshold || '-'} → {reward}</li>
                  })}
                </ul>
              </div>
            ) : (
              <div><strong>Reward:</strong> {[hasValidPoints && `${points} pts`, hasOffer && `Offer: ${rewardOfferName}`].filter(Boolean).join(' + ') || '-'}</div>
            )}
            {description && <div style={{ marginTop: '0.5rem' }}><strong>Description:</strong> {description}</div>}
          </div>
        </RunningListBox>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <SecondaryButton onClick={() => setStep(1)}>← Back</SecondaryButton>
          <PrimaryButton onClick={submitRequest} disabled={submitting || !canSubmit}>
            {submitting ? 'Submitting…' : 'Submit Request'}
          </PrimaryButton>
        </div>
      </div>
    )
  }

  const renderRecommendationsTab = () => {
    if (loadingRecommendations && !recommendations) return <Empty>Loading recommendations…</Empty>
    if (recommendationsError) return <ErrorBanner>{recommendationsError}</ErrorBanner>
    if (!recommendations) return <Empty>No data yet.</Empty>
    const scope = recommendations.scope
    if (recommendations.warming && recommendations.studios.length === 0) {
      return (
        <Empty>
          Computing recommendations… this can take 20-60s on first run. Auto-refreshing every 10s.
        </Empty>
      )
    }
    if (recommendations.studios.length === 0) {
      return (
        <Empty>
          No under-performing slots found across {scope?.studios_in_scope ?? '-'} studios in
          the last {scope?.lookback_days ?? 90} days. (Threshold: ≥15pp below network avg,
          ≥8 sessions of data.)
        </Empty>
      )
    }
    return (
      <div>
        {recommendations.warming && (
          <div style={{
            background: '#fff8e1', color: '#7a5b00', padding: '0.6rem',
            borderRadius: 4, fontSize: '1.05rem', marginBottom: '0.75rem',
          }}>
            Refreshing in the background - showing last cached results. This page will auto-update.
          </div>
        )}
        <div style={{
          background: '#f8f4ec',
          border: '1px solid #e6dcc6',
          borderRadius: 6,
          padding: '0.85rem 1rem',
          marginBottom: '1rem',
          fontSize: '0.95rem',
          color: '#5a4a2a',
          lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 700, marginBottom: '0.4rem', color: '#3a2c10' }}>How recommendations are calculated</div>
          <ol style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              Pull <strong>{scope?.lookback_days ?? 90} days</strong> of class attendance from
              Snowflake (HAL.MT_CLASSES + MT_ATTENDANCE) for {scope?.studios_in_scope ?? 72} studios
              in scope (US excl. Miami Beach + Midtown Miami, Canada, UK, Dublin).
            </li>
            <li>
              For each <em>(studio × class type × day-of-week × hour)</em> slot, compute the
              <strong> studio fill rate</strong> = avg(check-ins / capacity) and the
              <strong> network fill rate</strong> = same metric across all in-scope studios for the
              same slot pattern.
            </li>
            <li>
              <strong>Flag</strong> a slot when <code>network_fill − studio_fill ≥ 15&nbsp;pp</code>{' '}
              <em>and</em> the studio has ≥ 8 sessions of data in the window.{' '}
              (<em>pp</em> = percentage points - the absolute difference, not a relative %.)
            </li>
            <li>
              For each flagged slot, list up to 2 upcoming class instances at that studio in the
              next {scope?.upcoming_days ?? 14} days, fetched live from Mariana Tek so the
              class_session ids are real. Each studio is capped at the top 5 slots by gap.
            </li>
            <li>
              Tick the rows you want to seed a campaign with and click <em>Add Selected to Create</em>
              {' '}for that studio.
            </li>
          </ol>
          {recommendations.generated_at && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#7a6a4a' }}>
              Generated {new Date(recommendations.generated_at).toLocaleString('en-US')} · cached for 6h.
            </div>
          )}
        </div>
        {recommendations.studios.map((studio) => {
          const tz = studioTimeZone(studio.studio_name)
          const checkedCount = studio.slots.filter((s) => checkedRecs.has(s.mt_class_session_id)).length
          return (
            <ChallengeCard key={studio.studio_id}>
              <CardHeader>
                <CardTitle>
                  {studio.studio_name}
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', color: theme.color.text.grey, fontWeight: 400 }}>
                    {studio.slots.length} class instance{studio.slots.length === 1 ? '' : 's'} flagged
                  </span>
                </CardTitle>
                <PrimaryButton
                  onClick={() => addStudioRecsToCreate(studio)}
                  disabled={checkedCount === 0}
                >
                  Add Selected to Create ({checkedCount}) →
                </PrimaryButton>
              </CardHeader>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0' }}>
                {studio.slots.map((s) => {
                  const checked = checkedRecs.has(s.mt_class_session_id)
                  const studioPct = s.studio_fill_pct != null ? `${s.studio_fill_pct.toFixed(1)}%` : '-'
                  const networkPct = s.network_fill_pct != null ? `${s.network_fill_pct.toFixed(1)}%` : '-'
                  const gap = s.gap_pp != null ? `▼ ${s.gap_pp.toFixed(1)}pp` : ''
                  return (
                    <li
                      key={s.mt_class_session_id}
                      style={{
                        padding: '0.5rem 0.6rem',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        background: checked ? '#fff8f8' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRec(s.mt_class_session_id)}
                        style={{ marginTop: '0.15rem' }}
                      />
                      <div style={{ flex: 1, fontSize: '1.05rem', lineHeight: 1.5 }}>
                        <div>
                          <strong>{fmtDateTimeInTz(s.start_datetime, tz)}</strong>
                          <PeakBadge iso={s.start_datetime} tz={tz} />
                          {' - '}{s.class_name}
                          {' · '}<code style={{ fontSize: '0.9rem' }}>id {s.mt_class_session_id}</code>
                        </div>
                        <div style={{ color: theme.color.text.grey, fontSize: '0.95rem' }}>
                          Studio fill {studioPct} (across {s.studio_sessions ?? '?'} prior sessions)
                          {' · '}network avg {networkPct}
                          {' · '}<span style={{ color: '#b00020', fontWeight: 600 }}>{gap}</span>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </ChallengeCard>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <TriggerPill onClick={() => setIsOpen(true)} title="Indv Studio Challenges">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M12 2l2.39 7.36H22l-6.18 4.49L18.21 22 12 17.27 5.79 22l2.39-8.15L2 9.36h7.61z"/>
        </svg>
        Indv Studio Challenges
      </TriggerPill>

      {isOpen && (
        <ModalOverlay onClick={() => { setIsOpen(false); resetCreateState() }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <Header>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Title>Indv Studio Challenges</Title>
                {isAdmin && <button
                  onClick={() => setTab('create')}
                  style={{
                    height: 36,
                    padding: '0 1.1rem',
                    background: tab === 'create' ? '#9b001e' : theme.color.accent.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: 18,
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  }}
                >
                  + Create
                </button>}
              </div>
              <CloseButton onClick={() => { setIsOpen(false); resetCreateState() }}>×</CloseButton>
            </Header>
            <TabBar>
              <Tab active={tab === 'active'} onClick={() => setTab('active')}>Requested</Tab>
              <Tab active={tab === 'in_progress'} onClick={() => setTab('in_progress')}>In Progress</Tab>
              <Tab active={tab === 'completed'} onClick={() => setTab('completed')}>Completed</Tab>
              {isAdmin && <div style={{ marginLeft: 'auto' }}>
                <Tab active={tab === 'recommendations'} onClick={() => setTab('recommendations')}>Recommendations</Tab>
              </div>}
            </TabBar>
            <Body>
              {tab === 'active' && renderListTab('active')}
              {tab === 'in_progress' && renderListTab('in_progress')}
              {tab === 'completed' && renderListTab('completed')}
              {isAdmin && tab === 'create' && renderCreateTab()}
              {isAdmin && tab === 'recommendations' && renderRecommendationsTab()}
            </Body>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}

export default StudioChallengesButton
